import { IconAssigned } from '@components/icons/assigned';
import { FilterConfigField } from '@components/table';
import { FilterData } from '@components/table/filters';

import { useAuthenticatedUser } from 'features/authentication';
import { useStore } from 'models/Provider';
import { IUser } from 'models/types';

const assigneeFieldToExternalQuery = (filter: FilterData) => {
  switch (filter.comparer) {
    case 'is':
      if (filter.value === 'anyone') return { assigned_to__isnull: false };
      if (filter.value === 'none') return { assigned_to__isnull: true };
      return { assigned_to__term: filter.value };
    case 'any_of':
      return {
        assigned_to__terms: Array.isArray(filter.value) ? filter.value.join('__') : filter.value,
      };
    case 'not_is':
      return { assigned_to__exclude: filter.value };
    case 'none_of':
      return { assigned_to__exclude: filter.value };
    default:
      return {};
  }
};

export const useAssigneeFilter = (): FilterConfigField => {
  const { t, userStore } = useStore();
  const me = useAuthenticatedUser();

  return {
    id: 'assignee',
    label: t('applicants.filters.assigned.title'),
    icon: <IconAssigned />,
    type: 'user',
    values: (filter) => {
      if (filter.comparer === 'is') {
        const users = userStore.list.map((user: IUser) => ({
          value: String(user.id),
          label: user.name,
        }));
        return [
          { value: 'none', label: t('applicants.filters.assigned.none') },
          { value: 'anyone', label: t('applicants.filters.assigned.anyone') },
          ...users,
        ];
      }

      return userStore.list
        .map((user: any) => ({
          value: String(user.id),
          label: user.name,
        }))
        .sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label));
    },
    quick: [
      {
        id: 'me',
        label: t('applicants.filters.assigned.me'),
        apply: { comparer: 'is', value: String(me.id) },
      },
      {
        id: 'none',
        label: t('applicants.filters.assigned.none'),
        apply: { comparer: 'is', value: 'none' },
      },
      {
        id: 'anyone',
        label: t('applicants.filters.assigned.anyone'),
        apply: { comparer: 'is', value: 'anyone' },
      },
    ],
    allowedComparers: ['is', 'not_is', 'any_of', 'none_of'],
    toExternalQuery: assigneeFieldToExternalQuery,
  };
};
